import {Graphics, Rectangle} from "pixi.js";
import {inches} from "../../const";
import {CLOSER_FROM_HINGE_EDGE, OPERATOR_ARM_BELOW_TOP} from "../../model/hardware/MdHardwareRules";
import {glyphStyle, GlyphFootprintContext, GlyphLeaf, GlyphRect, hasCategory, HardwareGlyph, hitAreaOf, spanRect, strikeEdgeX} from "./HardwareGlyph";

// a surface automatic operator seen face-on with the doors closed: the header box
// mounted on the face of the head, standing up past the head's top, with an arm down to
// each leaf under it. Each arm is the main arm seen end-on (a stub from the box underside
// to the leaf), then the forearm lying along the leaf top from the pivot toward the strike
// edge to its shoe. The pivot hangs the same distance in from the hinge edge as a closer.
//
// A frame placement (on the head) draws the box over every leaf under the head with an
// arm to each; on a stick that is not a head only the box draws, centred on the hub. A
// door placement draws the box over its own leaf, just above the leaf top, with the hub
// as the arm pivot. Sizes are typical catalogue values (scene units = mm).
const HEADER_HEIGHT = 6 * inches;
const HEADER_ABOVE_HEAD = 2 * inches;       // the box stands at least this far above the head
const HEADER_END_OVERHANG = 1 * inches;     // box end past the outer leaf edges
const HEADER_LENGTH_ALONE = 36 * inches;    // box length with no leaf to span
const HEADER_GAP = 0.125 * inches;          // box underside above the leaf top (door placement)
const HEADER_RADIUS = 0.375 * inches;
const STUB_WIDTH = 1.25 * inches;           // the main arm seen end-on
const ARM_THICKNESS = 0.75 * inches;
const ARM_LENGTH = 12 * inches;             // pivot to the shoe, toward the strike edge
const SHOE_LENGTH = 2.5 * inches;
const SHOE_HEIGHT = 1 * inches;
const HUB_RADIUS = 0.5 * inches;
const OUTLINE = 0.25 * inches;
const HIT_PAD = 1 * inches;

// edge-view projections (scene units = mm): the header off the frame face and the main
// arm's thickness.
const HEADER_DEPTH = 5 * inches;
const ARM_DEPTH = 0.5 * inches;

export const surfaceOperator: HardwareGlyph = {
    id: "surfaceOperator",
    targets: ["door", "frame"],
    matches: item => hasCategory(item, "Surface Automatic Operator"),
    draw(g: Graphics, ctx): Rectangle {
        const {color, alpha} = glyphStyle(ctx.selected, ctx.invalid);
        const l = layout(ctx);

        for(const arm of l.arms) drawArm(g, color, alpha, l.box.y1, arm);
        drawHeader(g, color, alpha, l.box);

        // on the head, the hub so the placement point (the dimension reference) stays visible.
        if(ctx.target === "frame") {
            g.lineStyle(OUTLINE, color, alpha);
            g.beginFill(0, 0);
            g.drawCircle(ctx.hub.x, ctx.hub.y, HUB_RADIUS);
            g.endFill();
        }

        return hitAreaOf(footprint(ctx), HIT_PAD);
    },
    footprint,
    // seen from the latch edge: the header seated on the frame face on the item's side
    // (its underside at the bottom of the face return) and the main arm as an L: down
    // from the centre of the header's underside to the forearm height, then across to
    // the door face where the forearm (running away from the viewer along the leaf)
    // meets it. Without a head in the view the header stands off the door face above
    // the leaf top instead. A door placement's hub is the arm height; a head placement
    // hangs the arm the standard distance below the leaf top.
    drawEdge(g: Graphics, ctx): Rectangle {
        const {color, alpha} = glyphStyle(ctx.selected, ctx.invalid);
        const fx = ctx.face.x;
        const cy = ctx.target === "frame" ? ctx.face.y + OPERATOR_ARM_BELOW_TOP : ctx.face.y;
        const out = ctx.out;
        const head = ctx.head;

        const seatX = head ? (out === 1 ? head.x1 : head.x0) : fx;
        const boxBottom = head ? head.faceBottom : cy - OPERATOR_ARM_BELOW_TOP - HEADER_GAP;
        const boxTop = boxBottom - HEADER_HEIGHT;
        const dropX = seatX + out * HEADER_DEPTH / 2;

        // the drop, then the reach back to the door face.
        g.lineStyle(0);
        g.beginFill(color, alpha);
        g.drawRect(dropX - ARM_DEPTH / 2, boxBottom, ARM_DEPTH, cy - boxBottom);
        const reachX0 = Math.min(fx, dropX - ARM_DEPTH / 2);
        const reachX1 = Math.max(fx, dropX + ARM_DEPTH / 2);
        g.drawRect(reachX0, cy - ARM_DEPTH / 2, reachX1 - reachX0, ARM_DEPTH);
        g.endFill();

        g.lineStyle(OUTLINE, color, alpha);
        g.beginFill(color, alpha * 0.2);
        g.drawRoundedRect(Math.min(seatX, seatX + out * HEADER_DEPTH), boxTop, HEADER_DEPTH, HEADER_HEIGHT, HEADER_RADIUS);
        g.endFill();

        const x0 = Math.min(fx, seatX, seatX + out * HEADER_DEPTH);
        const x1 = Math.max(fx, seatX, seatX + out * HEADER_DEPTH);
        return new Rectangle(x0 - HIT_PAD, boxTop - HIT_PAD, x1 - x0 + 2 * HIT_PAD, cy + ARM_DEPTH / 2 - boxTop + 2 * HIT_PAD);
    },
};

// one arm: its pivot on the leaf (the forearm height) and the direction the forearm
// reaches, toward the strike edge.
type Arm = {pivotX: number; armY: number; dir: 1 | -1};

// the face-on geometry: the header box and the arm(s) hanging from it. A door placement
// puts the box over its own leaf with the hub as the arm pivot; a head placement spans the
// leaves under the head with an arm to each (only the box when there is no leaf).
function layout(ctx: GlyphFootprintContext): {box: GlyphRect; arms: Arm[]} {
    if(ctx.target === "door") {
        const leaf = ctx.rect;
        // `dir` points from the hinge edge toward the strike edge.
        const dir: 1 | -1 = strikeEdgeX(ctx).dir === 1 ? -1 : 1;
        const box: GlyphRect = {
            x0: leaf.x0 - HEADER_END_OVERHANG,
            x1: leaf.x1 + HEADER_END_OVERHANG,
            y1: leaf.y0 - HEADER_GAP,
            y0: leaf.y0 - HEADER_GAP - HEADER_HEIGHT,
        };
        return {box, arms: [{pivotX: ctx.hub.x, armY: ctx.hub.y, dir}]};
    }

    const head = ctx.rect;
    const leaves = leavesUnderHead(head, ctx.leaves);

    let box: GlyphRect;
    if(leaves.length > 0) {
        const x0 = Math.min(...leaves.map(l => l.rect.x0)) - HEADER_END_OVERHANG;
        const x1 = Math.max(...leaves.map(l => l.rect.x1)) + HEADER_END_OVERHANG;
        const y1 = head.y1;
        const y0 = Math.min(head.y0 - HEADER_ABOVE_HEAD, y1 - HEADER_HEIGHT);
        box = {x0, y0, x1, y1};
    } else {
        box = {
            x0: ctx.hub.x - HEADER_LENGTH_ALONE / 2,
            y0: ctx.hub.y - HEADER_HEIGHT / 2,
            x1: ctx.hub.x + HEADER_LENGTH_ALONE / 2,
            y1: ctx.hub.y + HEADER_HEIGHT / 2,
        };
    }

    const arms = leaves.map((leaf): Arm => {
        const dir = leaf.hingeSide === "left" ? 1 : -1;
        const hingeX = leaf.hingeSide === "left" ? leaf.rect.x0 : leaf.rect.x1;
        return {pivotX: hingeX + dir * CLOSER_FROM_HINGE_EDGE, armY: leaf.rect.y0 + OPERATOR_ARM_BELOW_TOP, dir};
    });

    return {box, arms};
}

// the header box, each arm (stub, forearm and shoe) from the box down onto its leaf, and
// the hub.
function footprint(ctx: GlyphFootprintContext): GlyphRect[] {
    const {box, arms} = layout(ctx);
    return [
        box,
        ...arms.map(arm => armRect(box.y1, arm)),
        {x0: ctx.hub.x - HUB_RADIUS, y0: ctx.hub.y - HUB_RADIUS, x1: ctx.hub.x + HUB_RADIUS, y1: ctx.hub.y + HUB_RADIUS},
    ];
}

// the extent of one arm hanging from `boxBottom`: the stub over the pivot, the forearm to
// the shoe and the shoe itself.
function armRect(boxBottom: number, arm: Arm): GlyphRect {
    const shoeX = arm.pivotX + arm.dir * ARM_LENGTH;
    const armHalf = Math.max(ARM_THICKNESS, SHOE_HEIGHT) / 2;
    return spanRect(
        [arm.pivotX - STUB_WIDTH / 2, arm.pivotX + STUB_WIDTH / 2, shoeX, shoeX - arm.dir * SHOE_LENGTH],
        [boxBottom, arm.armY - armHalf, arm.armY + armHalf],
    );
}

// one arm: the stub from `boxBottom` down to the forearm centred on the pivot, the
// forearm `dir`-ward along the leaf, and its shoe.
function drawArm(g: Graphics, color: number, alpha: number, boxBottom: number, arm: Arm) {
    const {pivotX, armY, dir} = arm;
    const shoeX = pivotX + dir * ARM_LENGTH;

    g.lineStyle(0);
    g.beginFill(color, alpha);
    g.drawRect(pivotX - STUB_WIDTH / 2, boxBottom, STUB_WIDTH, armY - boxBottom);
    g.endFill();

    g.lineStyle(OUTLINE, color, alpha);
    g.beginFill(color, alpha * 0.35);
    g.drawRect(Math.min(pivotX, shoeX), armY - ARM_THICKNESS / 2, ARM_LENGTH, ARM_THICKNESS);
    g.endFill();

    g.lineStyle(0);
    g.beginFill(color, alpha);
    g.drawRect(shoeX - dir * SHOE_LENGTH / 2 - SHOE_LENGTH / 2, armY - SHOE_HEIGHT / 2, SHOE_LENGTH, SHOE_HEIGHT);
    g.endFill();
}

function drawHeader(g: Graphics, color: number, alpha: number, box: GlyphRect) {
    g.lineStyle(OUTLINE, color, alpha);
    g.beginFill(color, alpha * 0.2);
    g.drawRoundedRect(box.x0, box.y0, box.x1 - box.x0, box.y1 - box.y0, HEADER_RADIUS);
    g.endFill();
}

// the leaves a head-mounted operator reaches: those hanging under a horizontal stick,
// with no leaf starting above the stick's bottom (the stick is a head, not a sill).
function leavesUnderHead(stick: GlyphRect, leaves: GlyphLeaf[]): GlyphLeaf[] {
    const horizontal = stick.x1 - stick.x0 > stick.y1 - stick.y0;
    if(!horizontal) return [];
    return leaves.filter(l => l.rect.y0 >= stick.y1 && l.rect.x1 > stick.x0 && l.rect.x0 < stick.x1);
}
